import { ThreeEvent, useThree } from '@react-three/fiber';
import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import rotationIconURL from '../../assets/icons/rotate_right.svg';
import { immersightBlue } from '../../utils/colors';

interface RotationSpriteProps {
  targetRef: React.RefObject<THREE.Group>;
  spriteScale?: [number, number, number];
  circleColor?: string;
  customOffset?: THREE.Vector3 | null;
  onStartRotate: () => void;
  onEndRotate: () => void;
}

const RotationSprite: React.FC<RotationSpriteProps> = ({
  targetRef,
  spriteScale = [2, 2, 1],
  circleColor = immersightBlue,
  customOffset,
  onStartRotate,
  onEndRotate,
}) => {
  const { camera, gl } = useThree();
  const spriteRef = useRef<THREE.Sprite>(null);
  const plane = useRef(new THREE.Plane());
  const raycaster = useRef(new THREE.Raycaster());
  const isRotating = useRef(false);
  const [initialMousePos, setInitialMousePos] = useState<THREE.Vector3 | null>(null);
  const [texture, setTexture] = useState<THREE.Texture | null>(null);

  /**
   * Track if Shift is pressed globally
   */
  const [isShiftPressed, setIsShiftPressed] = useState(false);
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Shift') setIsShiftPressed(true);
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Shift') setIsShiftPressed(false);
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, []);

  /**
   * A ref to store the local offset in the target’s coordinate system.
   */
  const localOffsetRef = useRef(new THREE.Vector3(0, 0, 0));

  /**
   * Create the circle-with-icon texture
   */
  useEffect(() => {
    const mainCanvas = document.createElement('canvas');
    const circleSize = 128;
    mainCanvas.width = circleSize;
    mainCanvas.height = circleSize;

    const mainCtx = mainCanvas.getContext('2d');
    if (!mainCtx) return;

    // Circle background
    mainCtx.fillStyle = circleColor;
    mainCtx.beginPath();
    mainCtx.arc(circleSize / 2, circleSize / 2, circleSize / 2, 0, Math.PI * 2);
    mainCtx.fill();

    // Offscreen canvas for icon recoloring
    const offCanvas = document.createElement('canvas');
    offCanvas.width = circleSize;
    offCanvas.height = circleSize;
    const offCtx = offCanvas.getContext('2d');
    if (!offCtx) return;

    const iconImg = new Image();
    iconImg.src = rotationIconURL;
    iconImg.onload = () => {
      offCtx.drawImage(iconImg, 0, 0, circleSize, circleSize);

      // Recolor to white
      offCtx.globalCompositeOperation = 'source-in';
      offCtx.fillStyle = '#FFFFFF';
      offCtx.fillRect(0, 0, circleSize, circleSize);
      offCtx.globalCompositeOperation = 'source-over';

      // Draw the icon into the circle background, centered
      const iconSize = circleSize * 0.6;
      const x = (circleSize - iconSize) / 2;
      const y = (circleSize - iconSize) / 2;
      mainCtx.drawImage(offCanvas, x, y, iconSize, iconSize);

      // Convert to Texture
      const canvasTexture = new THREE.CanvasTexture(mainCanvas);
      canvasTexture.needsUpdate = true;
      setTexture(canvasTexture);
    };
  }, [circleColor]);

  /**
   * Calculate initial sprite position based on the target's rotation and local offset
   */
  useEffect(() => {
    if (targetRef.current && spriteRef.current && texture) {
      // Ensure target's world matrix is updated
      targetRef.current.updateWorldMatrix(true, false);

      // Compute the local offset if not already done
      if (customOffset) {
        localOffsetRef.current.copy(customOffset);
      } else {
        const oldRotation = targetRef.current.rotation.clone();
        targetRef.current.rotation.set(0, 0, 0);
        targetRef.current.updateWorldMatrix(true, false);
        const box = new THREE.Box3().setFromObject(targetRef.current);
        const size = new THREE.Vector3();
        box.getSize(size);

        const offsetX = size.x / 2 + 3;
        localOffsetRef.current.set(offsetX, 0, 0);

        targetRef.current.rotation.copy(oldRotation);
        targetRef.current.updateWorldMatrix(true, false);
      }

      // Compute the world offset
      const worldOffset = localOffsetRef.current.clone();
      worldOffset.applyAxisAngle(new THREE.Vector3(0, 0, 1), targetRef.current.rotation.z);
      const center = new THREE.Vector3();
      targetRef.current.getWorldPosition(center);

      // Place the sprite
      spriteRef.current.position.copy(center.add(worldOffset));
    }
  }, [targetRef, texture, customOffset]);

  /**
   * Position sprite initially using localOffsetRef, converting to world space
   */
  useEffect(() => {
    if (!targetRef.current || !spriteRef.current) return;
    targetRef.current.updateWorldMatrix(true, false);

    const worldOffset = localOffsetRef.current.clone();
    targetRef.current.localToWorld(worldOffset);

    spriteRef.current.position.copy(worldOffset);
  }, [targetRef]);

  /**
   * Calculate/clamp mouse speed for rotation
   */
  const calculateMouseSpeed = (() => {
    let lastMousePos: THREE.Vector3 | null = null;
    let lastTimestamp: number | null = null;

    return (currentMousePos: THREE.Vector3, currentTimestamp: number): number => {
      if (!lastMousePos || !lastTimestamp) {
        lastMousePos = currentMousePos.clone();
        lastTimestamp = currentTimestamp;
        return 0.1;
      }
      const distance = currentMousePos.distanceTo(lastMousePos);
      const timeElapsed = currentTimestamp - lastTimestamp;
      if (timeElapsed <= 0) return 0.1;

      lastMousePos.copy(currentMousePos);
      lastTimestamp = currentTimestamp;
      return Math.max(0.05, Math.min((distance / timeElapsed) * 50, 1));
    };
  })();

  /**
   * Helper to convert screen coords to normalized device coords
   */
  const getNormalizedDeviceCoordinates = (x: number, y: number): THREE.Vector2 => {
    const rect = gl.domElement.getBoundingClientRect();
    return new THREE.Vector2(((x - rect.left) / rect.width) * 2 - 1, -((y - rect.top) / rect.height) * 2 + 1);
  };

  /**
   * Pointer Down
   */
  const handlePointerDown = (event: ThreeEvent<PointerEvent>) => {
    event.stopPropagation();

    if (!targetRef.current || !spriteRef.current) return;

    const center = new THREE.Vector3();
    targetRef.current.getWorldPosition(center);
    plane.current.setFromNormalAndCoplanarPoint(new THREE.Vector3(0, 0, 1), center);

    const ndc = getNormalizedDeviceCoordinates(event.clientX, event.clientY);
    raycaster.current.setFromCamera(ndc, camera);

    const intersection = new THREE.Vector3();
    if (raycaster.current.ray.intersectPlane(plane.current, intersection)) {
      setInitialMousePos(intersection);
      isRotating.current = true;
      onStartRotate();
      gl.domElement.style.cursor = 'grabbing';
      window.addEventListener('mousemove', handlePointerMove);
      window.addEventListener('mouseup', handlePointerUp);
    }
  };

  /**
   * Pointer Move (drag rotation)
   */
  const handlePointerMove = (event: MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();

    if (!isRotating.current || !targetRef.current || !initialMousePos) return;

    const center = new THREE.Vector3();
    targetRef.current.getWorldPosition(center);

    const ndc = getNormalizedDeviceCoordinates(event.clientX, event.clientY);
    raycaster.current.setFromCamera(ndc, camera);

    const intersection = new THREE.Vector3();
    if (!raycaster.current.ray.intersectPlane(plane.current, intersection)) return;

    const currentPosRelative = spriteRef.current?.position.clone().sub(center).normalize();
    const newPosRelative = intersection.clone().sub(center).normalize();
    if (!currentPosRelative || !newPosRelative) return;

    // Calculate the angle difference
    let angle = Math.acos(currentPosRelative.dot(newPosRelative));
    const crossZ = new THREE.Vector3().crossVectors(currentPosRelative, newPosRelative).z;
    angle *= crossZ >= 0 ? 1 : -1;

    const mouseSpeed = calculateMouseSpeed(intersection, performance.now());
    const rotationSpeedFactor = Math.min(mouseSpeed, 1.5);

    /**
     * if Shift is pressed, skip the "1 degree threshold" so
     * we can keep rotating in small increments. Then snap.
     */
    if (isShiftPressed) {
      // Accumulate the rotation
      targetRef.current.rotation.z += angle * rotationSpeedFactor;

      // Snap to 15-degree increments
      const rotationDeg = THREE.MathUtils.radToDeg(targetRef.current.rotation.z);
      const snappedDeg = Math.round(rotationDeg / 15) * 15;
      targetRef.current.rotation.z = THREE.MathUtils.degToRad(snappedDeg);
    } else {
      const effectiveAngle = angle * rotationSpeedFactor;
      const minEffectiveAngleThreshold = 0.01;
      if (Math.abs(effectiveAngle) > minEffectiveAngleThreshold) {
        targetRef.current.rotation.z += effectiveAngle;
      }
    }

    // Normalize rotation to [0, 2π]
    targetRef.current.rotation.z = ((targetRef.current.rotation.z % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);

    // Update sprite position
    const spriteOffset = localOffsetRef.current.clone();
    spriteOffset.applyAxisAngle(new THREE.Vector3(0, 0, 1), targetRef.current.rotation.z);
    spriteRef.current?.position.copy(center.add(spriteOffset));

    setInitialMousePos(intersection);
  };

  /**
   * Pointer Up
   */
  const handlePointerUp = () => {
    if (targetRef.current) {
      // Final clamp rotation.z to [0, 2π]
      targetRef.current.rotation.z = ((targetRef.current.rotation.z % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
    }

    isRotating.current = false;
    onEndRotate();
    gl.domElement.style.cursor = 'default';
    window.removeEventListener('mousemove', handlePointerMove);
    window.removeEventListener('mouseup', handlePointerUp);
  };

  /**
   * Cursor Handling
   */
  const handlePointerOver = (event: ThreeEvent<PointerEvent>) => {
    event.stopPropagation();
    if (!isRotating.current) {
      gl.domElement.style.cursor = 'grab';
    }
  };

  const handlePointerOut = (event: ThreeEvent<PointerEvent>) => {
    event.stopPropagation();
    if (!isRotating.current) {
      gl.domElement.style.cursor = 'default';
    }
  };

  return (
    <>
      {texture && (
        <sprite
          renderOrder={9999}
          ref={spriteRef}
          scale={spriteScale}
          onPointerDown={handlePointerDown}
          onPointerOver={handlePointerOver}
          onPointerOut={handlePointerOut}
        >
          <spriteMaterial attach="material" map={texture} depthWrite={false} />
        </sprite>
      )}
    </>
  );
};

export default RotationSprite;